'use client';

import { c as _c } from "react/compiler-runtime";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { getEntityById } from '@src/actions/content.actions';
import PostWorkoutExperience from '@src/components/PostWorkoutExperience/PostWorkoutExperience';
import { configPublic } from '@src/config.public';
import { useSession } from '@src/hooks/useSession';
import { useLocale } from '@src/i18n/LocaleProvider';
import { type ParentContent, getEntityQueryKey } from '@src/utils/query.util';
import { refreshAfterWorkoutQueries } from '@src/utils/refresh-after-workout.util';

// 10 min fallback duration @ 25% threshold = 2.5 minute QV
import { instrumentAction } from "@src/utils/instrumentation";
const FALLBACK_VIDEO_DURATION_SECONDS = 600;
interface PostWorkoutExperienceController {
  setPostWorkoutEntityId: (entityId?: string, dayId?: string) => void;
  setPostWorkoutParentContent: (parentContent?: ParentContent | null) => void;
  reportVideoProgress: (progress: number, duration?: number) => void;
  startPostWorkout: () => boolean;
  /** CDP parent content synced from child ContentDetailsDataProvider for PWE favorite button state. */
  postWorkoutParentContent?: ParentContent | null;
}
interface PostWorkoutExperienceViewState {
  isOpen: boolean;
  isQVThresholdReached: boolean;
  entityData?: CocoApi.PlayableEntity;
  percentProgress?: number;
  close: () => void;
}
type PostWorkoutExperienceContextValue = PostWorkoutExperienceController & PostWorkoutExperienceViewState;
const PostWorkoutExperienceContext = createContext<PostWorkoutExperienceContextValue | undefined>(undefined);
export const PostWorkoutExperienceProvider: React.FC<{
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(56);
  const {
    children
  } = t0;
  const {
    user
  } = useSession();
  const {
    locale
  } = useLocale();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [isQVThresholdReached, setIsQVThresholdReached] = useState(false);
  const [entityId, setEntityId] = useState();
  const [dayId, setDayId] = useState();
  const [postWorkoutParentContent, setPostWorkoutParentContentState] = useState();
  const [percentProgress, setPercentProgress] = useState();
  const percentProgressRef = useRef(undefined);
  let t1;
  if ($[0] !== dayId || $[1] !== entityId || $[2] !== isQVThresholdReached || $[3] !== postWorkoutParentContent) {
    t1 = {
      entityId,
      dayId,
      postWorkoutParentContent,
      isQVThresholdReached
    };
    $[0] = dayId;
    $[1] = entityId;
    $[2] = isQVThresholdReached;
    $[3] = postWorkoutParentContent;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const workoutRefreshContextRef = useRef(t1);
  let t2;
  let t3;
  if ($[5] !== dayId || $[6] !== entityId || $[7] !== isQVThresholdReached || $[8] !== postWorkoutParentContent) {
    t2 = () => {
      workoutRefreshContextRef.current = {
        entityId,
        dayId,
        postWorkoutParentContent,
        isQVThresholdReached
      };
    };
    t3 = [entityId, dayId, postWorkoutParentContent, isQVThresholdReached];
    $[5] = dayId;
    $[6] = entityId;
    $[7] = isQVThresholdReached;
    $[8] = postWorkoutParentContent;
    $[9] = t2;
    $[10] = t3;
  } else {
    t2 = $[9];
    t3 = $[10];
  }
  useEffect(t2, t3);
  let t4;
  if ($[11] !== locale.code || $[12] !== queryClient) {
    t4 = () => {
      const ctx = workoutRefreshContextRef.current;
      if (!ctx.isQVThresholdReached) {
        return;
      }
      refreshAfterWorkoutQueries(queryClient, {
        locale: locale.code,
        parentContent: ctx.postWorkoutParentContent,
        entityId: ctx.entityId,
        dayId: ctx.dayId
      });
    };
    $[11] = locale.code;
    $[12] = queryClient;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const refreshAfterWorkout = t4;
  let t5;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setIsQVThresholdReached(false);
      setEntityId(undefined);
      setDayId(undefined);
      setPercentProgress(undefined);
      percentProgressRef.current = undefined;
    };
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const resetWorkoutSession = t5;
  const t6 = entityId as string;
  let t7;
  if ($[15] !== locale.code || $[16] !== t6) {
    t7 = getEntityQueryKey(t6, locale.code);
    $[15] = locale.code;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== entityId || $[19] !== locale.code) {
    t8 = () => instrumentAction("ContentActions.getEntityById", getEntityById)({
      entityId: entityId as string,
      locale: locale.code
    });
    $[18] = entityId;
    $[19] = locale.code;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const t9 = Boolean(isQVThresholdReached && entityId);
  let t10;
  if ($[21] !== t7 || $[22] !== t8 || $[23] !== t9) {
    t10 = {
      queryKey: t7,
      queryFn: t8,
      enabled: t9
    };
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const {
    data: entityData
  } = useQuery(t10);
  let t11;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = (videoEntityId, videoDayId) => {
      setEntityId(videoEntityId);
      setDayId(videoDayId);
    };
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const setPostWorkoutEntityId = t11;
  let t12;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = parentContent => setPostWorkoutParentContentState(parentContent);
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const setPostWorkoutParentContent = t12;
  let t13;
  if ($[27] !== isQVThresholdReached) {
    t13 = (progress, duration) => {
      const progressPercentage = progress / (duration ?? FALLBACK_VIDEO_DURATION_SECONDS) * 100;
      percentProgressRef.current = progressPercentage;
      if (progressPercentage >= configPublic.videoPlayer.qvThresholdPercentage && !isQVThresholdReached) {
        setIsQVThresholdReached(true);
      }
    };
    $[27] = isQVThresholdReached;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const reportVideoProgress = t13;
  let t14;
  if ($[29] !== entityData?._type || $[30] !== entityId || $[31] !== isQVThresholdReached || $[32] !== refreshAfterWorkout || $[33] !== user?.guid) {
    t14 = () => {
      const shouldOpen = Boolean(user?.guid && isQVThresholdReached && entityData?._type === "workout" && entityId);
      if (shouldOpen) {
        setPercentProgress(percentProgressRef.current);
        setIsOpen(true);
      } else {
        refreshAfterWorkout();
        resetWorkoutSession();
      }
      return shouldOpen;
    };
    $[29] = entityData?._type;
    $[30] = entityId;
    $[31] = isQVThresholdReached;
    $[32] = refreshAfterWorkout;
    $[33] = user?.guid;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  user?.guid;
  entityData?._type;
  const startPostWorkout = t14;
  let t15;
  if ($[35] !== refreshAfterWorkout) {
    t15 = () => {
      refreshAfterWorkout();
      setIsOpen(false);
      resetWorkoutSession();
    };
    $[35] = refreshAfterWorkout;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  const close = t15;
  const t16 = entityData as CocoApi.PlayableEntity | undefined;
  let t17;
  if ($[37] !== close || $[38] !== isOpen || $[39] !== isQVThresholdReached || $[40] !== percentProgress || $[41] !== postWorkoutParentContent || $[42] !== reportVideoProgress || $[43] !== startPostWorkout || $[44] !== t16) {
    t17 = {
      setPostWorkoutEntityId,
      setPostWorkoutParentContent,
      reportVideoProgress,
      startPostWorkout,
      isOpen,
      isQVThresholdReached,
      entityData: t16,
      percentProgress,
      close,
      postWorkoutParentContent
    };
    $[37] = close;
    $[38] = isOpen;
    $[39] = isQVThresholdReached;
    $[40] = percentProgress;
    $[41] = postWorkoutParentContent;
    $[42] = reportVideoProgress;
    $[43] = startPostWorkout;
    $[44] = t16;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  const value = t17;
  const t18 = entityData as CocoApi.PlayableEntity;
  let t19;
  if ($[46] !== close || $[47] !== isOpen || $[48] !== isQVThresholdReached || $[49] !== percentProgress || $[50] !== t18) {
    t19 = <PostWorkoutExperience isOpen={isOpen} handleClose={close} entityData={t18} isQVThresholdReached={isQVThresholdReached} percentProgress={percentProgress} />;
    $[46] = close;
    $[47] = isOpen;
    $[48] = isQVThresholdReached;
    $[49] = percentProgress;
    $[50] = t18;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== children || $[53] !== t19 || $[54] !== value) {
    t20 = <PostWorkoutExperienceContext.Provider value={value}>{children}{t19}</PostWorkoutExperienceContext.Provider>;
    $[52] = children;
    $[53] = t19;
    $[54] = value;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  return t20;
};
function usePostWorkoutExperienceContext() {
  const context = useContext(PostWorkoutExperienceContext);
  if (!context) {
    throw new Error("usePostWorkoutExperience must be used within a PostWorkoutExperienceProvider");
  }
  return context;
}
export function usePostWorkoutExperienceController() {
  const $ = _c(6);
  const {
    setPostWorkoutEntityId,
    setPostWorkoutParentContent,
    reportVideoProgress,
    startPostWorkout,
    postWorkoutParentContent
  } = usePostWorkoutExperienceContext();
  let t0;
  if ($[0] !== postWorkoutParentContent || $[1] !== reportVideoProgress || $[2] !== setPostWorkoutEntityId || $[3] !== setPostWorkoutParentContent || $[4] !== startPostWorkout) {
    t0 = {
      setPostWorkoutEntityId,
      setPostWorkoutParentContent,
      reportVideoProgress,
      startPostWorkout,
      postWorkoutParentContent
    };
    $[0] = postWorkoutParentContent;
    $[1] = reportVideoProgress;
    $[2] = setPostWorkoutEntityId;
    $[3] = setPostWorkoutParentContent;
    $[4] = startPostWorkout;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  return t0;
}
export function usePostWorkoutExperienceViewState() {
  const $ = _c(6);
  const {
    isOpen,
    isQVThresholdReached,
    entityData,
    percentProgress,
    close
  } = usePostWorkoutExperienceContext();
  let t0;
  if ($[0] !== close || $[1] !== entityData || $[2] !== isOpen || $[3] !== isQVThresholdReached || $[4] !== percentProgress) {
    t0 = {
      isOpen,
      isQVThresholdReached,
      entityData,
      percentProgress,
      close
    };
    $[0] = close;
    $[1] = entityData;
    $[2] = isOpen;
    $[3] = isQVThresholdReached;
    $[4] = percentProgress;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  return t0;
}